// PublicSiteShell — premium navigation + footer shared by Engineering, Corporate Hub and About sections.
// The shell is configured per-division (logos, brand colours, menu) so we present a coherent
// multi-division identity while keeping a single codebase.
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Mail, Phone, MapPin } from 'lucide-react';
import AIChatWidget from '@/components/AIChatWidget';

const HQS = [
  { city: 'Lagos', state: 'Lagos State', country: 'Nigeria', flag: '🇳🇬' },
  { city: 'London', state: 'England', country: 'United Kingdom', flag: '🇬🇧' },
  { city: 'Houston', state: 'Texas', country: 'United States', flag: '🇺🇸' },
  { city: 'Toronto', state: 'Ontario', country: 'Canada', flag: '🇨🇦' },
];

export default function PublicSiteShell({ brand, menu, children, chatContext }) {
  return (
    <div className="bg-white text-neutral-900" data-testid={`public-shell-${brand.shortName.toLowerCase().replace(/\s+/g, '-')}`}>
      <PremiumNav brand={brand} menu={menu} />
      {children}
      <PublicFooter brand={brand} />
      <AIChatWidget context={chatContext || `Visitor on ${brand.shortName} public site.`} />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Premium navigation with division-specific branding and dropdowns      */
/* -------------------------------------------------------------------- */

function PremiumNav({ brand, menu }) {
  const [open, setOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const { pathname } = useLocation();

  return (
    <header
      className="sticky top-0 z-40 w-full border-b"
      style={{ background: brand.primary, borderColor: 'rgba(255,255,255,0.08)' }}
      data-testid="public-nav"
    >
      <div className="container-x flex items-center justify-between h-20 lg:h-24">
        {/* Brand block */}
        <Link to={menu[0]?.href || '/'} className="flex items-center gap-3 group" data-testid="public-nav-brand">
          <div className="h-14 w-14 lg:h-16 lg:w-16 rounded-md bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center p-1.5 group-hover:bg-white/15 transition-colors">
            <img src={brand.logo} alt={brand.shortName} className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-base lg:text-lg font-bold tracking-tight text-white">
              {brand.shortName}
            </div>
            <div className="text-[10px] lg:text-[11px] uppercase tracking-[0.22em] hidden sm:block" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {brand.division}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="public-nav-menu">
          {menu.map((item) => {
            const active = item.href && pathname === item.href;
            if (item.dropdown) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setOpenDrop(item.label)}
                  onMouseLeave={() => setOpenDrop(null)}
                >
                  <button
                    className="px-4 py-2.5 text-sm font-medium tracking-tight inline-flex items-center gap-1 rounded-md transition-colors text-white hover:bg-white/10"
                    data-testid={`nav-drop-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label} <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {openDrop === item.label && (
                    <div
                      className="absolute top-full left-0 mt-1 w-72 rounded-md shadow-2xl border py-2 z-50"
                      style={{ background: '#FFFFFF', borderColor: 'rgba(0,0,0,0.08)' }}
                    >
                      {item.dropdown.map((d) => (
                        <Link
                          key={d.href}
                          to={d.href}
                          className="block px-5 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors border-l-2 border-transparent hover:border-current"
                          style={{ borderLeftColor: pathname === d.href ? brand.accent : 'transparent', color: pathname === d.href ? brand.accent : undefined }}
                          data-testid={`nav-drop-link-${d.href.replace(/\//g, '-').slice(1)}`}
                        >
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.href}
                className="px-4 py-2.5 text-sm font-medium tracking-tight rounded-md transition-colors text-white hover:bg-white/10"
                style={active ? { background: `${brand.accent}25`, color: brand.accent } : {}}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/" className="text-xs uppercase tracking-wider hover:opacity-80 transition-opacity" style={{ color: 'rgba(255,255,255,0.7)' }}>
            ← ATTP NEXUS
          </Link>
          <Link
            to="/login"
            className="px-5 py-2.5 rounded-md font-semibold text-sm transition-all hover:scale-[1.03]"
            style={{ background: brand.accent, color: brand.primary }}
            data-testid="public-nav-login"
          >
            Login
          </Link>
        </div>

        <button className="lg:hidden p-2 text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="public-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t" style={{ background: brand.primary, borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="container-x py-4 flex flex-col gap-1.5">
            {menu.map((item) => (
              item.dropdown ? (
                <details key={item.label} className="text-sm">
                  <summary className="cursor-pointer py-2 px-2 font-medium text-white">{item.label}</summary>
                  <div className="pl-4 flex flex-col gap-1">
                    {item.dropdown.map((d) => (
                      <Link key={d.href} to={d.href} onClick={() => setOpen(false)}
                        className="text-sm py-1.5 px-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                        {d.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link key={item.label} to={item.href} onClick={() => setOpen(false)}
                  className="text-sm py-2 px-2 font-medium text-white">
                  {item.label}
                </Link>
              )
            ))}
            <Link to="/" onClick={() => setOpen(false)} className="text-xs uppercase tracking-wider py-2 px-2" style={{ color: 'rgba(255,255,255,0.55)' }}>
              ← ATTP NEXUS
            </Link>
            <Link to="/login" onClick={() => setOpen(false)}
              className="mt-2 px-4 py-2.5 rounded-md font-semibold text-sm text-center"
              style={{ background: brand.accent, color: brand.primary }}>
              Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* -------------------------------------------------------------------- */
/* Public footer                                                         */
/* -------------------------------------------------------------------- */

function PublicFooter({ brand }) {
  return (
    <footer style={{ background: brand.primary, color: '#FFFFFF' }} data-testid="public-footer">
      <div className="container-x py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-14 w-14 rounded-md bg-white/10 border border-white/10 p-1.5 flex items-center justify-center">
                <img src={brand.logo} alt={brand.shortName} className="h-full w-full object-contain" />
              </div>
              <div>
                <div className="font-display text-lg font-bold">{brand.shortName}</div>
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {brand.division}
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed max-w-md" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {brand.tagline}
            </p>
            <div className="mt-6 space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4" style={{ color: brand.accent }} /> {brand.email}</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4" style={{ color: brand.accent }} /> {brand.phone}</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: brand.accent }} /> {brand.address}</div>
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: brand.accent }}>
              Headquarters
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {HQS.map((hq) => (
                <div key={hq.country} className="flex items-start gap-2 px-3 py-2 rounded border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <span className="text-xl">{hq.flag}</span>
                  <div>
                    <div className="text-sm font-semibold">{hq.city}</div>
                    <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{hq.state}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: brand.accent }}>
              Platform
            </div>
            <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
              <li><Link to="/" className="hover:opacity-80">ATTP NEXUS</Link></li>
              <li><Link to="/engineering" className="hover:opacity-80">Engineering Division</Link></li>
              <li><Link to="/corporate" className="hover:opacity-80">Corporate Hub</Link></li>
              <li><Link to="/about/about-attp" className="hover:opacity-80">About Us</Link></li>
              <li><Link to="/login" className="hover:opacity-80">Login</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-3" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
            © {new Date().getFullYear()} {brand.legalName}. All rights reserved.
          </div>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Part of the ATTP NEXUS platform
          </div>
        </div>
      </div>
    </footer>
  );
}
